import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Card,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  CircularProgress,
  TextField,
  Snackbar,
  Alert,
  Fade,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';


// 🛠️ FIX APPLIED HERE: Updated Helper function for 3-state status (Done, Missing, Not Entered)
const MilestoneStatus = ({ label, status }) => {
  // Normalize the status
  const val = status !== undefined && status !== null ? String(status).toLowerCase().trim() : "";

  // Define exactly what counts as "Done"
  const SUCCESS_STATUSES = ['issued', 'found', 'done', '1', 'true', 'yes', 'received'];
  const isDone = SUCCESS_STATUSES.includes(val);

  // If it's not done, it's red. Period.
  const color = isDone ? '#4CAF50' : '#F44336'; 
  const indicator = isDone ? '✅' : '❌';

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="caption" sx={{ fontWeight: 'bold', color: color, display: 'flex' }}>
        {indicator}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
        {label}
      </Typography>
    </Stack>
  );
};


MatterCard.propTypes = {
  matter: PropTypes.object.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdateStatus: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  statusTooltips: PropTypes.object.isRequired,
  isDeletedView: PropTypes.bool,
  onRestore: PropTypes.func,
  // Corrected propTypes structure to expect a 'milestones' object
  milestones: PropTypes.shape({
    provincalCourt:PropTypes.string,
    provisionalCertificate: PropTypes.string,
    finalCertificate: PropTypes.string,
    firstMeeting: PropTypes.string,
    secondMeeting: PropTypes.string,
    ldAccount: PropTypes.string,
    confirmation: PropTypes.string,
  }),
};

export default function MatterCard({ matter, onDelete, onUpdateStatus, isAdmin, statusTooltips, isOldDatabase, milestones = {} }) {
  const { id, matterName, status, documents = [], caseNumber, client, estateNo } = matter;
  const navigate = useNavigate();

  // State hooks
  const [openModal, setOpenModal] = useState(false);
  const [newStatus, setNewStatus] = useState(status);
  const [loading, setLoading] = useState(false);

  // Invite Guest state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  // 🛠️ NEW STATE: Internal state for fetched milestones (initialized with prop data)
  const [fetchedMilestones, setFetchedMilestones] = useState(() => {
    // Try to populate from matter prop if milestones prop is empty
    if (Object.keys(milestones).length > 0) return milestones;
    
    return {
      provisionalCertificate: matter.provisionalCertificate,
      provincalCourt: matter.provincalCourt || matter.provincialCourt || matter.courtOrder,
      finalCertificate: matter.finalCertificate,
      firstMeeting: matter.firstMeeting,
      secondMeeting: matter.secondMeeting,
      ldAccount: matter.ldAccount,
      confirmation: matter.confirmation,
    };
  });

  // Destructure the required variables from the current milestone data
  const {
  provisionalCertificate = "",
  provincalCourt = "", 
  finalCertificate = "",
  firstMeeting = "",
  secondMeeting = "",
  ldAccount = "",
  confirmation = ""
} = fetchedMilestones || {};

  // Message state for Snackbar
  const [statusMessage, setStatusMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const statusColors = {
    'in progress': 'green',
    pending: 'orange',
    closed: 'red',
  };

  const handleClick = () => {
    // Check the database type to navigate to the correct detail page
    const type = isOldDatabase ? 'estate' : 'matter';
    navigate(`/${type}/${id}`);
  };

  const handleDeleteClick = (event) => {
    event.stopPropagation();
    onDelete(id);
  };

  const handleStatusChange = (event) => {
    setNewStatus(event.target.value);
  };

  const handleStatusUpdate = async () => {
    try {
      setLoading(true);
      await onUpdateStatus(id, newStatus);
      setOpenModal(false);
      setStatusMessage('Status updated successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating status:', error);
      setStatusMessage('Failed to update status. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
                const fetchMilestones = async () => {
                  // Determine the correct endpoint and ID based on database type
                  // const endpoint = isOldDatabase 
                  //   ? `/api/old-database/milestones/${id}` 
                  //   : `/api/legal-matter/milestones/${id}`;
                  const endpoint = `/api/legal-matter/milestones/${id}`;
                  
                  try {
                    // Assuming the endpoint returns a JSON object with all six milestone statuses
                    const response = await fetch(`https://ipms.insollex.com${endpoint}`);
                    
                    if (!response.ok) throw new Error('Failed to fetch milestones');
                    
                    const data = await response.json();
                    
                    // 💡 ADDED CONSOLE LOG FOR DEBUGGING
                    console.log("Milestone Data Received:", data);
                    
                    // Update internal state with fetched data
                    setFetchedMilestones(data);
            
                  } catch (error) {
                    console.error(`Error fetching milestones for ${id}:`, error);
                  }
                };
                
                // Only fetch if the component received a valid ID
                if (id) {
                  fetchMilestones();
                }
              }, [id, isOldDatabase]); // Dependency array is correctly set

  const handleInviteGuest = async () => {
  try {
    setInviteLoading(true);
    const inviteUrl = `/matter/${id}/invite`;

    const response = await fetch(inviteUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: guestEmail }),
    });

    const data = await response.json();

    if (response.ok) {
      setStatusMessage('Guest invited! Redirecting to Mail List...');
      setSnackbarOpen(true);
      
      // Close modal and clear input
      setInviteDialogOpen(false);
      setGuestEmail('');

      // 🚀 REDIRECT: Send the user to the Mail List page
      setTimeout(() => {
        navigate('/mail-list'); 
      }, 1500);
      
    } else {
      setStatusMessage(`Invite failed: ${data.message}`);
      setSnackbarOpen(true);
    }
  } catch (error) {
    console.error('Error:', error);
    setStatusMessage('Something went wrong.');
    setSnackbarOpen(true);
  } finally {
    setInviteLoading(false);
  }
};

  useEffect(() => {
  if (milestones && Object.keys(milestones).length > 0) {
    setFetchedMilestones(milestones);
  }
}, [milestones]);

  const tooltipDescription = statusTooltips[status.toLowerCase()] || "No description available.";

  return (
    <>
      <Card
        // 🛠️ FIX: Increased height from 250px to 320px to accommodate all content and buttons
        sx={{ height: 320, display: 'flex', flexDirection: 'column', position: 'relative' }}
      >

        <Stack spacing={1} sx={{ p: 3, flex: 1, overflow: 'hidden' }}>
          <Link
            href="#"
            color="inherit"
            underline="hover"
            onClick={handleClick}
          >
            <Typography variant="subtitle2" noWrap>
              {matterName}
            </Typography>
          </Link>

          <Stack direction="row" spacing={10}>
            {estateNo && (
              <Typography variant="body2" color="text.secondary">
                Estate Num: {estateNo}
              </Typography>
            )}
            {client && (
              <Typography variant="body2" color="text.secondary">
                Username: {client}
              </Typography>
            )}
          </Stack>

          {/* Milestone Statuses */}
          {/* Reduced gap to 0.5 for compactness */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <MilestoneStatus
              label="Provincal Court Order"
              status={provincalCourt}
            />
            <MilestoneStatus
              label="Provisional Cert."
              status={provisionalCertificate}
            />
            <MilestoneStatus
              label="Final Order Cert."
              status={finalCertificate}
            />
            <MilestoneStatus
              label="1st Meeting"
              status={firstMeeting}
            />
            <MilestoneStatus
              label="2nd Meeting"
              status={secondMeeting}
            />
            <MilestoneStatus
              label="L&D Account"
              status={ldAccount}
            />
            <MilestoneStatus
              label="Confirmation"
              status={confirmation}
            />
          </Box>


          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {documents.length > 0 ? `${documents.length} documents` : "No documents"}
            </Typography>
          </Stack>

          {/* This stack contains the status pill and the buttons */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
            
            {/* 🔴 COMMENTED OUT: Status Pill and Tooltip */}
            {/* <Tooltip title={tooltipDescription} arrow>
              <Box
                sx={{
                  zIndex: 9,
                  position: 'relative',
                  textTransform: 'uppercase',
                  backgroundColor: statusColors[status.toLowerCase()] || 'gray',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  minWidth: '80px',
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                {status}
              </Box>
            </Tooltip>
            */}

            {isAdmin && (
              <Stack direction="row" spacing={1}>
                {/* 🔴 COMMENTED OUT: Update Status Button */}
                {/* <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    px: 1.5,
                    minWidth: 'unset',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                  onClick={() => setOpenModal(true)}
                >
                  Update Status
                </Button>
                */}
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    px: 1.5,
                    minWidth: 'unset',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                  onClick={() => setInviteDialogOpen(true)}
                >
                  Invite Guest
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Card>

      {/* 🔴 COMMENTED OUT: Modal: Update Status */}
      {/* <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Fade in={openModal}>
          <Box sx={{
            bgcolor: 'background.paper',
            padding: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: 300
          }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>Update Status</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={newStatus}
                label="Status"
                onChange={handleStatusChange}
              >
                <MenuItem value="in progress">In Progress</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2} sx={{ marginTop: 2 }}>
              <Button onClick={() => setOpenModal(false)} color="secondary" variant="outlined">Cancel</Button>
              <Button
                onClick={handleStatusUpdate}
                color="primary"
                variant="contained"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
              </Button>
            </Stack>
          </Box>
        </Fade>
      </Modal>
      */}

      {/* Modal: Invite Guest */}
      <Modal
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Fade in={inviteDialogOpen}>
          <Box sx={{
            bgcolor: 'background.paper',
            padding: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: 300
          }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>Invite Guest</Typography>
            <TextField
              fullWidth
              label="Guest Email"
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              sx={{ marginBottom: 2 }}
            />
            <Stack direction="row" spacing={2}>
              <Button onClick={() => setInviteDialogOpen(false)} color="secondary" variant="outlined">Cancel</Button>
              <Button
                onClick={handleInviteGuest}
                color="primary"
                variant="contained"
                disabled={inviteLoading}
              >
                {inviteLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Invite'}
              </Button>
            </Stack>
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={statusMessage.includes('failed') ? 'error' : 'success'}>
          {statusMessage}
        </Alert>
      </Snackbar>
    </>
  );
}